import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Bookmark, Eye, Heart, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type {
  CommentCount,
  CommentCountList,
  CommentPreviewGroup,
  CommentPreviewList,
  FeedCountsList,
  LikeSummary,
  LikeSummaryList,
} from '@up2d8/contracts';
import { Avatar, cn, useToast } from '@up2d8/ui';
import { useSession } from '../../shared/session';

/**
 * De tellerrij onder een bericht, en de like-knop erin (Stap 12b).
 *
 * Drie soorten getallen die uit twee modules komen: weergaven en bevestigingen uit `reach`, likes
 * en reacties uit `interaction`. Voor de lezer is dat één rij — hij ziet geen modules.
 *
 * Wat hier nooit staat: een naam. Niet bij de weergaven (ADR-0014, dat is monitoring) en ook niet
 * bij de likes (geen lijst "wie heeft geliket", ADR-0048). Alleen aantallen.
 */

const POSTS_PER_BATCH = 50;

/**
 * De ids in brokken van vijftig, het maximum dat de server per aanroep aanneemt.
 *
 * Een feed die verder gescrold is dan vijftig kaarten, stuurde eerst alles in één keer (400) of
 * kapte af op vijftig — en dan verloren de kaarten daarna hun tellers.
 */
export function inBatches(postIds: string[]): string[][] {
  const batches: string[][] = [];
  for (let start = 0; start < postIds.length; start += POSTS_PER_BATCH) {
    batches.push(postIds.slice(start, start + POSTS_PER_BATCH));
  }
  return batches;
}

/** Likes van een hele feedpagina in één aanroep; de sleutel hangt aan de ids, niet aan de pagina. */
export function useLikes(postIds: string[]) {
  const { api } = useSession();
  return useQuery({
    queryKey: ['likes', postIds],
    enabled: postIds.length > 0,
    queryFn: async (): Promise<LikeSummary[]> => {
      const pages = await Promise.all(
        inBatches(postIds).map(async (batch) => {
          const { data, error } = await api.GET('/api/v1/feed/likes', {
            params: { query: { postIds: batch.join(',') } },
          });
          // Een mislukte tellerrij mag de feed niet omver halen: zonder getallen is het bericht nog
          // steeds leesbaar, en lezen is waar deze app voor is.
          if (error || !data) return [];
          return (data as LikeSummaryList).items;
        }),
      );
      return pages.flat();
    },
  });
}

/** Weergaven en bevestigingen; `suppressed` betekent: toon helemaal niets (ADR-0049). */
export function useFeedCounts(postIds: string[]) {
  const { api } = useSession();
  return useQuery({
    queryKey: ['feed-counts', postIds],
    enabled: postIds.length > 0,
    queryFn: async () => {
      const pages = await Promise.all(
        inBatches(postIds).map(async (batch) => {
          const { data, error } = await api.GET('/api/v1/feed/counts', {
            params: { query: { postIds: batch.join(',') } },
          });
          if (error || !data) return [];
          return (data as FeedCountsList).items;
        }),
      );
      return pages.flat();
    },
  });
}

/** Het aantal reacties per bericht, voor een hele feedpagina in één aanroep. */
export function useCommentCounts(postIds: string[]) {
  const { api } = useSession();
  return useQuery({
    queryKey: ['comment-counts', postIds],
    enabled: postIds.length > 0,
    queryFn: async (): Promise<CommentCount[]> => {
      const pages = await Promise.all(
        inBatches(postIds).map(async (batch) => {
          const { data, error } = await api.GET('/api/v1/feed/comment-counts', {
            params: { query: { postIds: batch.join(',') } },
          });
          if (error || !data) return [];
          return (data as CommentCountList).items;
        }),
      );
      return pages.flat();
    },
  });
}

/**
 * Het aantal reacties én de laatste drie, voor een hele feedpagina (Extra 5).
 *
 * Vervangt `useCommentCounts` op de kaart. Die hook blijft bestaan voor de berichtpagina, waar
 * alleen het getal nodig is - daar staat de volledige lijst er toch al onder.
 */
export function useCommentPreviews(postIds: string[]) {
  const { api } = useSession();
  return useQuery({
    queryKey: ['comment-previews', postIds],
    enabled: postIds.length > 0,
    queryFn: async (): Promise<CommentPreviewGroup[]> => {
      const pages = await Promise.all(
        inBatches(postIds).map(async (batch) => {
          const { data, error } = await api.GET('/api/v1/feed/comment-previews', {
            params: { query: { postIds: batch.join(',') } },
          });
          if (error || !data) return [];
          return (data as CommentPreviewList).items;
        }),
      );
      return pages.flat();
    },
  });
}

export function useToggleLike(postId: string) {
  const { api } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (liked: boolean) => {
      // `liked` is de stand die de gebruiker wíl, niet de huidige. Zo is de aanroep idempotent aan
      // beide kanten: tweemaal dezelfde wens geeft tweemaal dezelfde uitkomst.
      const { error } = liked
        ? await api.PUT('/api/v1/feed/{postId}/like', { params: { path: { postId } } })
        : await api.DELETE('/api/v1/feed/{postId}/like', { params: { path: { postId } } });
      if (error) throw error;
    },
    // Optimistisch: de knop springt meteen. Een like is geen bevestiging — er hangt geen bewijs aan,
    // dus een tik hoort niet op het netwerk te wachten.
    onMutate: async (liked) => {
      const previous = queryClient.getQueriesData<LikeSummary[]>({ queryKey: ['likes'] });
      queryClient.setQueriesData<LikeSummary[]>({ queryKey: ['likes'] }, (rows) =>
        (rows ?? []).map((row) =>
          row.postId === postId
            ? { ...row, likedByMe: liked, count: Math.max(0, row.count + (liked ? 1 : -1)) }
            : row,
        ),
      );
      return { previous };
    },
    onError: (_error, _liked, context) => {
      // Terugdraaien naar precies wat er stond. Een teller die blijft hangen op een getal dat de
      // server niet kent, is erger dan een tik die niet aankwam.
      for (const [key, value] of context?.previous ?? []) {
        queryClient.setQueryData(key, value);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['likes'] });
    },
  });
}

/**
 * Een bericht opslaan of niet meer (Opgeslagen berichten).
 *
 * Eén hook voor de drie plaatsen waar het kan: het bericht op de telefoon, op het bureauscherm en
 * het "…"-menu op de kaart. Eerst stond de aanroep alleen in het bureaudetail, waardoor er op de
 * telefoon geen knop was terwijl de lege lijst ernaar verwees (testrapport 28 okt., #11).
 *
 * `saved` is de stand die de gebruiker wíl, zoals bij de like: zo is de aanroep idempotent. Daarna
 * gaan de berichten opnieuw op, want `savedByMe` zit in het bericht zelf en in `['feed', 'saved']`.
 */
export function useToggleSaved(postId: string) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (saved: boolean) => {
      const params = { path: { postId } };
      const { error } = saved
        ? await api.PUT('/api/v1/feed/{postId}/saved', { params })
        : await api.DELETE('/api/v1/feed/{postId}/saved', { params });
      if (error) throw error;
      return saved;
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['feed'] });
      toast.show({
        tone: 'success',
        title: saved ? t('interaction.savedToast') : t('interaction.unsavedToast'),
      });
    },
    onError: () => toast.show({ tone: 'danger', title: t('interaction.saveFailed') }),
  });
}

/**
 * De knop "Opslaan" onder een bericht op de telefoon. Een raakvlak van 48 px, zoals de like ernaast.
 */
export function SaveButton({ postId, saved }: { postId: string; saved: boolean }) {
  const { t } = useTranslation();
  const toggle = useToggleSaved(postId);

  return (
    <button
      type="button"
      data-touch-target
      data-testid="save-button"
      aria-pressed={saved}
      disabled={toggle.isPending}
      onClick={() => toggle.mutate(!saved)}
      className={cn(
        'inline-flex min-h-touch items-center gap-1.5 rounded-md px-2 py-1 text-small transition-colors',
        'hover:bg-surface-sunk focus-visible:outline focus-visible:outline-2',
        'focus-visible:outline-offset-2 focus-visible:outline-brand-primary',
        saved ? 'text-brand-primary' : 'text-text-muted',
      )}
    >
      <Bookmark aria-hidden className={cn('h-5 w-5', saved && 'fill-current')} />
      {saved ? t('interaction.saved') : t('interaction.save')}
    </button>
  );
}

export interface InteractionBarProps {
  postId: string;
  like: LikeSummary | undefined;
  viewCount: number | null;
  commentCount: number | null;
  /** Of de aantallen onderdrukt zijn omdat de doelgroep te klein is (ADR-0041). */
  suppressed: boolean;
}

/**
 * De rij met tellers en de like-knop.
 *
 * Bij `suppressed` verdwijnen weergaven — en alleen weergaven. Likes en reacties blijven staan: dat
 * zijn handelingen van wie ze plaatst en geen meting van wie wat las, dus de k-drempel geldt er
 * niet voor. Er komt geen "0" en geen uitleg in de plaats van een onderdrukt getal: beide zouden
 * meer verklappen dan het getal zelf.
 */
export function InteractionBar({
  postId,
  like,
  viewCount,
  commentCount,
  suppressed,
}: InteractionBarProps) {
  const { t } = useTranslation();
  const toggle = useToggleLike(postId);
  const liked = like?.likedByMe ?? false;
  const likeCount = like?.count ?? 0;

  return (
    <div
      data-testid="interaction-bar"
      className="flex items-center gap-1 text-small text-text-muted"
    >
      <button
        type="button"
        data-touch-target
        data-testid="like-button"
        aria-pressed={liked}
        aria-label={liked ? t('interaction.unlike') : t('interaction.like')}
        onClick={() => toggle.mutate(!liked)}
        className={cn(
          'inline-flex min-h-touch items-center gap-1.5 rounded-md px-2 py-1 transition-colors',
          'hover:bg-surface-sunk focus-visible:outline focus-visible:outline-2',
          'focus-visible:outline-offset-2 focus-visible:outline-brand-primary',
          liked && 'text-brand-primary',
        )}
      >
        <Heart aria-hidden className={cn('h-5 w-5', liked && 'fill-current')} />
        {/* Nul verbergen: een lege kaart met drie nullen leest als een bericht dat niemand las. */}
        {likeCount > 0 && <span data-testid="like-count">{likeCount}</span>}
      </button>

      <span
        data-testid="comment-count"
        className="inline-flex min-h-touch items-center gap-1.5 px-2 py-1"
      >
        <MessageCircle aria-hidden className="h-5 w-5" />
        {commentCount !== null && commentCount > 0 && <span>{commentCount}</span>}
      </span>

      {!suppressed && viewCount !== null && (
        <span
          data-testid="view-count"
          className="inline-flex min-h-touch items-center gap-1.5 px-2 py-1"
          title={t('interaction.viewsHint')}
        >
          <Eye aria-hidden className="h-5 w-5" />
          <span>{viewCount}</span>
        </span>
      )}
    </div>
  );
}

export interface AcknowledgeProgressProps {
  acknowledgedCount: number | null;
  recipientCount: number | null;
  suppressed: boolean;
}

/**
 * "18 van 32 bevestigd" onder een bericht met bevestigingsplicht.
 *
 * Alleen bij bevestigingsplicht, en alleen boven de k-drempel. Onder een gewone aankondiging zou
 * "0 van 32" lezen als een achterstand die niet bestaat — daarom levert de server daar `null`.
 */
export function AcknowledgeProgress({
  acknowledgedCount,
  recipientCount,
  suppressed,
}: AcknowledgeProgressProps) {
  const { t } = useTranslation();
  if (suppressed || acknowledgedCount === null || recipientCount === null) return null;

  return (
    <p data-testid="acknowledge-progress" className="text-small text-text-muted">
      {t('interaction.acknowledgedOf', { done: acknowledgedCount, total: recipientCount })}
    </p>
  );
}

export interface CommentPreviewStripProps {
  postId: string;
  group: CommentPreviewGroup | undefined;
}

/**
 * De laatste reacties onder een kaart in de tijdlijn (Extra 5).
 *
 * Lezen, niet doen: er staat geen like-knop en geen invoerveld. Wie wil reageren, tikt door naar
 * het bericht - eerst lezen, dan reageren, en bij een bericht met leesbevestiging is dat geen
 * detail maar de hele bedoeling.
 *
 * Zonder reacties staat hier niets: de knop "Reageer" in de balk erboven zegt al genoeg, en een
 * lege strook met "nog geen reacties" maakt elke kaart langer zonder iets toe te voegen.
 */
export function CommentPreviewStrip({ postId, group }: CommentPreviewStripProps) {
  const { t } = useTranslation();
  if (!group || group.latest.length === 0) return null;

  return (
    <div
      data-testid="comment-preview"
      className="mt-2 flex flex-col gap-2 border-t border-border-light pt-2"
    >
      {group.latest.map((reactie) => (
        <div key={reactie.id} className="flex items-start gap-2">
          <Avatar name={reactie.authorName} size="sm" className="mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-small text-text-muted">
              <span className="font-semibold text-text-primary">{reactie.authorName}</span>
              {reactie.department ? <span> · {reactie.department}</span> : null}
              {/*
                "antwoord aan Lotte" in plaats van een boomstructuur: op een kaart van drie regels
                is inspringen niet te volgen, en de ouder staat er vaak niet eens bij.
              */}
              {reactie.replyToName ? (
                <span data-testid="preview-reply-to">
                  {' '}
                  ↳ {t('interaction.replyTo', { name: reactie.replyToName })}
                </span>
              ) : null}
            </p>
            {/* Twee regels: de server kortte de tekst al in, dit houdt ook een korte tekst met
                harde regeleindes binnen de kaart. */}
            <p className="line-clamp-2 text-content text-text-secondary">{reactie.body}</p>
          </div>
        </div>
      ))}

      <div className="flex items-center gap-2">
        <Link
          to="/app/posts/$postId"
          params={{ postId }}
          hash="reacties"
          data-testid="preview-all-link"
          className="min-h-touch inline-flex items-center text-small font-semibold text-brand-primary hover:underline"
        >
          {t('interaction.viewAllComments', { count: group.count })}
        </Link>
        {/* Gesloten reacties: de bestaande blijven staan, er komt niets bij (Stap 26). */}
        {!group.commentsEnabled ? (
          <span data-testid="preview-closed" className="text-small text-text-muted">
            {t('interaction.commentsClosed')}
          </span>
        ) : null}
      </div>
    </div>
  );
}
